import { formatIn, timeIn, wallParts } from "../../../utils/restaurantClock";
// What the Order Details page says about an order: its phase, status chip,
// banner, timeline and receipt lines. Pure functions, so the screen stays
// layout and the rules can be tested (orderProgress.test.js).

const n = (v) => parseFloat(v || 0) || 0;
const round2 = (v) => Math.round(v * 100) / 100;

/** A timestamp as stored (ISO string, Date, or milliseconds as a string). */
export function toDate(v) {
  if (v == null || v === "") return null;
  const d = new Date(/^\d+$/.test(String(v)) ? parseInt(v, 10) : v);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Order times are on the restaurant's clock (utils/restaurantClock.js); `tz`
// is order.restaurant.timezone. A customer in another zone sees the zone too.
export const orderTz = (o) => o?.restaurant?.timezone || null;

/** "6:42 PM" ("6:42 PM CST" when that isn't this browser's clock), or null. */
export const timeText = (v, tz) => {
  const d = toDate(v);
  return d ? timeIn(d, tz) : null;
};

/** "Tuesday, Oct 29" (with the year when it isn't this year). */
export const dayText = (v, tz) => {
  const d = toDate(v);
  if (!d) return null;
  const opts = { weekday: "long", month: "short", day: "numeric" };
  if (wallParts(d, tz).y !== wallParts(new Date(), tz).y) opts.year = "numeric";
  return formatIn(d, tz, opts);
};

/** "8 min", "1 hr 5 min"; null when either end is missing or out of order. */
export function durationText(from, to) {
  const a = toDate(from);
  const b = toDate(to);
  if (!a || !b || b < a) return null;
  const mins = Math.max(1, Math.round((b - a) / 60000));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

/** "+1 (424) 280-3696" for a US number; anything else as written. */
export function formatPhone(p) {
  const raw = String(p || "").trim();
  const digits = raw.replace(/\D/g, "");
  const ten = digits.length === 11 && digits[0] === "1" ? digits.slice(1) : digits.length === 10 ? digits : null;
  return ten ? `+1 (${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : raw;
}

/** The number to dial: digits with a leading +. */
export const telHref = (p) => {
  const digits = String(p || "").replace(/[^\d+]/g, "");
  return digits ? `tel:${digits.startsWith("+") ? digits : digits.length === 10 ? `+1${digits}` : `+${digits}`}` : null;
};

/** Rung up on our POS at the restaurant. */
export const isInStoreOrder = (o) => o?.orderType === "POS_COUNTER" || o?.orderType === "POS_DINE_IN";
/** A dine-in check reaches a customer when it is paid, and nothing moves it on from there. */
export const isPaidDineIn = (o) => o?.orderType === "POS_DINE_IN" && o?.paymentStatus === "PAID";
export const isPickupOrder = (o) => Boolean(o?.isPickedUp) || isInStoreOrder(o);

/** live, complete or cancelled. A collected pickup stays at PICKED for good. */
export function orderPhase(o) {
  const s = o?.orderStatus;
  if (s === "CANCELLED") return "cancelled";
  if (s === "DELIVERED" || s === "COMPLETED") return "complete";
  if ((isPickupOrder(o) && s === "PICKED") || isPaidDineIn(o)) return "complete";
  return "live";
}

/**
 * Who brings it: a restaurant staff driver, else RushMyFood (our riders and
 * First Delivery, which is branded as RushMyFood). Before anyone is assigned,
 * a self-delivery order is the restaurant's.
 */
export function deliverySource(o) {
  if (o?.rider?.driverType === "RESTAURANT") return "RESTAURANT";
  if (o?.rider?.driverType === "RUSHMYFOOD") return "RUSHMYFOOD";
  if (o?.firstDeliveryOrderId) return "RUSHMYFOOD";
  if (o?.isSelfDelivery) return "RESTAURANT";
  return "RUSHMYFOOD";
}

const LIVE_WORDS = {
  PAYMENT_PENDING: "Confirming payment",
  SCHEDULED: "Scheduled",
  PENDING: "Placed",
  ACCEPTED: "Preparing",
  PREPARED: "Food ready",
  ASSIGNED: "Driver assigned",
  EN_ROUTE_PICKUP: "Driver assigned",
  ARRIVED_PICKUP: "At restaurant",
  PICKED: "On the way",
  EN_ROUTE_DROPOFF: "On the way",
  ARRIVED_DROPOFF: "At your door",
};
const PRE_COLLECT = ["PREPARED", "ASSIGNED", "EN_ROUTE_PICKUP", "ARRIVED_PICKUP"];

/** The chip beside the title: { label, tone: live | complete | cancelled }. */
export function statusChip(o) {
  const phase = orderPhase(o);
  if (phase === "cancelled") return { label: "Cancelled", tone: "cancelled" };
  if (phase === "complete") return { label: isInStoreOrder(o) ? "Completed" : isPickupOrder(o) ? "Picked up" : "Delivered", tone: "complete" };
  if (isPickupOrder(o) && PRE_COLLECT.includes(o.orderStatus)) return { label: "Ready for pickup", tone: "live" };
  return { label: LIVE_WORDS[o.orderStatus] || "In progress", tone: "live" };
}

// Minutes until the kitchen's estimate (acceptedAt + preparationTime), or null.
function prepMinutesLeft(o, now) {
  const accepted = toDate(o.acceptedAt);
  const prep = parseInt(o.preparationTime, 10);
  if (!accepted || !prep) return null;
  return Math.floor((accepted.getTime() + prep * 60000 - now) / 60000);
}

const fmtEta = (iso, tz) => timeText(iso, tz);

/**
 * The coloured banner: { tone, eyebrow, headline, sub, tiles: [{ label, value }] }.
 * `lateText` is the translated "Sorry! Your order is bit late."
 */
export function statusBanner(o, { now = Date.now(), lateText = "Sorry! Your order is a bit late." } = {}) {
  const phase = orderPhase(o);
  const pickup = isPickupOrder(o);
  const placed = timeText(o.createdAt, orderTz(o));
  const tiles = [];
  if (placed) tiles.push({ label: "Placed", value: placed });

  if (phase === "cancelled") {
    const at = timeText(o.cancelledAt, orderTz(o));
    if (at) tiles.push({ label: "Cancelled", value: at });
    return { tone: "cancelled", eyebrow: "ORDER CANCELLED", headline: "This order was cancelled", sub: refundLine(o), tiles };
  }

  if (phase === "complete" && isInStoreOrder(o)) {
    return {
      tone: "complete",
      eyebrow: "IN-STORE ORDER",
      headline: "Order complete",
      sub: `Thanks for visiting ${o.restaurant?.name || "the restaurant"}.`,
      tiles,
    };
  }

  if (phase === "complete") {
    const doneAt = pickup ? o.deliveredAt || o.pickedAt || o.completedAt : o.deliveredAt || o.completedAt;
    const at = timeText(doneAt, orderTz(o));
    if (at) tiles.push({ label: pickup ? "Picked up" : "Delivered", value: at });
    const took = durationText(o.createdAt, doneAt);
    if (took) tiles.push({ label: "Total time", value: took });
    return {
      tone: "complete",
      eyebrow: "ORDER COMPLETE",
      headline: at ? `${pickup ? "Picked up" : "Delivered"} at ${at}` : pickup ? "You picked up your order" : "Your order was delivered",
      sub: "Enjoy your meal! Let us know how it was below.",
      tiles,
    };
  }

  const s = o.orderStatus;
  const restaurant = o.restaurant?.name || "The restaurant";
  const driver = o.rider?.name || o.fdDriverName || null;
  const selfDelivery = deliverySource(o) === "RESTAURANT";
  if (isInStoreOrder(o)) {
    const ready = PRE_COLLECT.includes(s);
    return {
      tone: "live",
      eyebrow: "IN-STORE ORDER",
      headline: ready ? "Ready for pickup" : "Preparing your order",
      sub: ready ? `Your order is ready at ${restaurant}.` : `${restaurant} is preparing your order.`,
      tiles,
    };
  }
  let headline;
  let sub;
  if (s === "SCHEDULED") {
    const when = toDate(o.orderDate);
    headline = pickup ? "Your pickup is scheduled" : "Your delivery is scheduled";
    sub = when ? `For ${dayText(when, orderTz(o))} at ${timeText(when, orderTz(o))}.` : "We'll start on it closer to the time.";
  } else if (s === "PAYMENT_PENDING") {
    headline = "Confirming your payment";
    sub = "This only takes a moment.";
  } else if (s === "PENDING") {
    headline = `Waiting for ${restaurant} to accept`;
    sub = "We'll let you know as soon as they confirm.";
  } else if (s === "ACCEPTED") {
    headline = "The restaurant is preparing your order";
    const left = prepMinutesLeft(o, now);
    sub = pickup
      ? "We'll let you know when it's ready for pickup."
      : selfDelivery
        ? "A driver will be assigned shortly."
        : "We'll assign a driver as soon as the food is ready.";
    if (left != null && left < -60) sub = lateText;
    else if (left != null && left > 0) tiles.push({ label: "Ready in", value: `~${left} min` });
  } else if (pickup && PRE_COLLECT.includes(s)) {
    headline = "Your order is ready for pickup";
    sub = `Head to ${restaurant} to collect it.`;
  } else if (s === "PREPARED") {
    headline = "Your food is ready";
    sub = "Waiting for a driver to pick it up.";
  } else if (s === "ASSIGNED" || s === "EN_ROUTE_PICKUP") {
    headline = "Your driver is heading to the restaurant";
    sub = driver ? `${driver} will pick up your order next.` : "They'll pick up your order next.";
  } else if (s === "ARRIVED_PICKUP") {
    headline = "Your driver is at the restaurant";
    sub = "Picking up your order now.";
  } else if (s === "PICKED" || s === "EN_ROUTE_DROPOFF") {
    headline = "Your order is on the way";
    sub = driver ? `${driver} is heading to you.` : "Your driver is heading to you.";
  } else if (s === "ARRIVED_DROPOFF") {
    headline = "Your driver is at your door";
    sub = "Arriving now!";
  } else {
    headline = "Your order is in progress";
    sub = "We'll keep this page up to date.";
  }
  const pickupEta = fmtEta(o.fdPickupEta, orderTz(o));
  const dropoffEta = fmtEta(o.fdDropoffEta, orderTz(o));
  if (pickupEta && !["PICKED", "EN_ROUTE_DROPOFF", "ARRIVED_DROPOFF"].includes(s)) tiles.push({ label: "Pickup ETA", value: pickupEta });
  if (dropoffEta) tiles.push({ label: "Delivery ETA", value: dropoffEta });
  return { tone: "live", eyebrow: `LIVE · ${statusChip(o).label.toUpperCase()}`, headline, sub, tiles };
}

const money = (v) => `$${n(v).toFixed(2)}`;

/** A cancelled order's refund, in words, when we know it. */
export function refundLine(o) {
  const card = n(o.refundedToCard);
  const wallet = n(o.refundedAmount);
  if (card > 0 && wallet > 0) return `${money(card)} was refunded to your card and ${money(wallet)} to your RMF Wallet.`;
  if (card > 0) return `${money(card)} was refunded to your card.`;
  if (wallet > 0) return `${money(wallet)} was added to your RMF Wallet.`;
  if (o.supportTicketStatus === "RESOLVED") return "A refund was issued to your RMF Wallet.";
  return "You can reorder it or get help with this order.";
}

// Each step: key, label, the time it happened.
function restaurantSteps(o) {
  return [
    { key: "placed", label: "Order placed", at: o.createdAt },
    { key: "accepted", label: "Accepted", at: o.acceptedAt },
    { key: "ready", label: isPickupOrder(o) ? "Ready for pickup" : "Food ready", at: o.preparedAt || (isPickupOrder(o) ? null : o.arrivedPickupAt) },
  ];
}
function deliverySteps(o) {
  return [
    { key: "assigned", label: "Driver assigned", at: o.assignedAt },
    // Heading to the restaurant starts the moment a driver takes it.
    { key: "toRestaurant", label: "On the way to restaurant", at: o.enRoutePickupAt || o.assignedAt },
    { key: "atRestaurant", label: "At restaurant", at: o.arrivedPickupAt },
    { key: "picked", label: "Order picked up", at: o.pickedAt },
    { key: "toYou", label: "On the way to you", at: o.enRouteDropoffAt || o.pickedAt },
    { key: "atDoor", label: "At your door", at: o.arrivedDropoffAt },
    { key: "delivered", label: "Delivered", at: o.deliveredAt },
  ];
}

// Where each group is up to: steps before this index are done, the step at it
// is happening now. Past the end = all done; -1 = not started.
function restaurantCursor(o, phase) {
  if (phase === "complete") return 99;
  const s = o.orderStatus;
  if (isPickupOrder(o)) {
    if (["PENDING", "PAYMENT_PENDING", "SCHEDULED"].includes(s)) return 1;
    if (s === "ACCEPTED") return 2;
    return 3; // ready, waiting to be collected
  }
  const foodReady = Boolean(o.preparedAt) || ["PICKED", "EN_ROUTE_DROPOFF", "ARRIVED_DROPOFF"].includes(s);
  if (foodReady) return 99;
  if (["PENDING", "PAYMENT_PENDING", "SCHEDULED"].includes(s)) return 1;
  if (s === "PREPARED") return 99;
  return 2; // accepted, or a driver is on the way while the kitchen cooks
}
const DELIVERY_CURSOR = { ASSIGNED: 1, EN_ROUTE_PICKUP: 1, ARRIVED_PICKUP: 2, PICKED: 4, EN_ROUTE_DROPOFF: 4, ARRIVED_DROPOFF: 5 };

/**
 * The vertical timeline: { groups: [{ key, title, steps: [{ key, label, time, state }] }], currentKey }.
 * state: done | current | future | cancelled. Only one step is the page's
 * current step (aria-current); while a driver heads over before the food is
 * ready, "Food ready" is still marked as happening.
 */
export function orderTimeline(o) {
  const phase = orderPhase(o);
  const pickup = isPickupOrder(o);
  const source = deliverySource(o);
  const shape = (steps, cursor) =>
    steps.map((st, i) => ({
      key: st.key,
      label: st.label,
      time: timeText(st.at, orderTz(o)),
      state: i < cursor ? "done" : i === cursor && phase === "live" ? "current" : "future",
    }));

  const rest = restaurantSteps(o);
  if (pickup) rest.push({ key: "collected", label: isInStoreOrder(o) ? "Completed" : "Picked up", at: o.deliveredAt || o.pickedAt });
  const groups = [{ key: "restaurant", title: isInStoreOrder(o) ? "IN-STORE" : pickup ? "PICKUP" : "RESTAURANT", steps: shape(rest, restaurantCursor(o, phase)) }];
  if (!pickup) {
    const title =
      phase === "complete"
        ? source === "RESTAURANT" ? "DELIVERED BY RESTAURANT" : "DELIVERED BY RUSHMYFOOD"
        : source === "RESTAURANT" ? "RESTAURANT DELIVERY" : "RUSHMYFOOD DELIVERY";
    const cursor = phase === "complete" ? 99 : DELIVERY_CURSOR[o.orderStatus] ?? -1;
    groups.push({ key: "delivery", title, steps: shape(deliverySteps(o), cursor) });
  }

  if (phase === "cancelled") {
    // Only what actually happened, then the cancellation.
    const happened = groups
      .map((g) => ({ ...g, steps: g.steps.filter((st) => st.time).map((st) => ({ ...st, state: "done" })) }))
      .filter((g) => g.steps.length);
    const last = happened[happened.length - 1] || { key: "restaurant", title: "RESTAURANT", steps: [] };
    if (!happened.length) happened.push(last);
    last.steps.push({ key: "cancelled", label: "Cancelled", time: timeText(o.cancelledAt, orderTz(o)), state: "cancelled" });
    return { groups: happened, currentKey: null };
  }

  // The page's one current step: the driver's, once there is one.
  const delivery = groups.find((g) => g.key === "delivery");
  const current = (delivery && delivery.steps.find((st) => st.state === "current")) || groups[0].steps.find((st) => st.state === "current");
  return { groups, currentKey: current ? current.key : null };
}

/** One item's price: (size + every option and group fee, any depth) × quantity. */
function nestedTotal(groups) {
  return (groups || []).reduce(
    (sum, g) =>
      sum +
      n(g.additionalCost) +
      (g.options || []).reduce((s, opt) => s + n(opt.price) * (parseInt(opt.quantity, 10) || 1) + nestedTotal(opt.addons), 0),
    0
  );
}
export function itemLineTotal(item) {
  const size = n(item.variation?.discounted) > 0 ? n(item.variation.discounted) : n(item.variation?.price);
  return round2((size + nestedTotal(item.addons)) * (parseInt(item.quantity, 10) || 1));
}

/**
 * The receipt's breakdown, built from what the server stored so the total is
 * what was charged and always the sum of the lines.
 *
 * The server keeps orderAmount NET of the promotion, the loyalty coupon and
 * wallet credit, and deliveryCharges NET of the Black Card discount
 * (resolvers/order.js placeOrder). The old receipt took the promotion and the
 * Black Card discount off a second time, so the total disagreed with the lines.
 *
 * Returns { lines: [{ key, label, amount, tone?, free?, note? }], total }.
 */
export function receiptLines(o) {
  const pickup = isPickupOrder(o);
  const wallet = n(o.walletAmountUsed);
  const promo = n(o.promotion?.discountAmount ?? o.promotionDiscount);
  const loyalty = n(o.loyaltyCouponDiscount);
  const blackCard = n(o.blackCardDiscount);
  const delivery = pickup ? 0 : n(o.deliveryCharges);
  const lines = [{ key: "subtotal", label: "Subtotal", amount: round2(n(o.orderAmount) + wallet + promo + loyalty) }];
  if (!pickup) {
    lines.push({
      key: "delivery",
      label: "Delivery fee",
      amount: delivery,
      free: delivery === 0,
      note: blackCard > 0 ? `Black Card saved you ${money(blackCard)}` : null,
    });
  }
  if (n(o.tipping) > 0) lines.push({ key: "tip", label: "Tip", amount: n(o.tipping) });
  if (n(o.taxationAmount) > 0) lines.push({ key: "tax", label: "Tax", amount: n(o.taxationAmount) });
  if (n(o.serviceFee) > 0) lines.push({ key: "service", label: "Service fee", amount: n(o.serviceFee) });
  if (promo > 0) lines.push({ key: "promo", label: o.promotion?.code ? `Discount (${o.promotion.code})` : "Discount", amount: -promo, tone: "green" });
  if (loyalty > 0) lines.push({ key: "loyalty", label: "Loyalty coupon", amount: -loyalty, tone: "green" });
  // Wallet credit is how part of it was paid, so it comes off last.
  if (wallet > 0) lines.push({ key: "wallet", label: "RMF Wallet used", amount: -wallet });
  const total = round2(Math.max(0, lines.reduce((sum, l) => sum + l.amount, 0)));
  return { lines, total };
}

/** "Paid with Visa ....1881" and the wallet part, for the box under the total. */
export function paidWith(o) {
  const wallet = n(o.walletAmountUsed);
  const { total } = receiptLines(o);
  const cash = String(o.paymentMethod || "").toUpperCase() === "COD";
  const label = o.paymentLabel || (cash ? "Cash" : "Card");
  if (wallet > 0 && total === 0) return { text: "Paid with RMF Wallet", wallet: 0 };
  if (/^paid\b/i.test(label)) return { text: label, wallet };
  const live = orderPhase(o) === "live";
  return { text: `${cash && live ? "Paying with" : "Paid with"} ${label}`, wallet };
}

/** Items refunded through a resolved ticket: name (lowercase) → quantity. */
export function refundedQuantities(o) {
  const m = new Map();
  if (o.supportTicketStatus !== "RESOLVED") return m;
  try {
    JSON.parse(o.refundedItems || "[]").forEach((i) => m.set(String(i.name || "").toLowerCase(), i.refundQty || i.qty || 0));
  } catch (e) {
    // An unreadable list shows no refund marks rather than breaking the receipt.
  }
  return m;
}

export const DROPOFF_LABELS = {
  LEAVE_AT_DOOR: "Leave at my door",
  MEET_AT_DOOR: "Meet me at the door",
  HAND_IT_TO_ME: "Hand it to me",
};
